import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { UserCheck, UserX, Send, Trash2, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: string;
  status: string;
  joinDate: string;
  propertiesCount: number;
  userId: string;
  isActive: boolean;
}

interface UsersManagementProps {
  users: User[];
  onToggleUserStatus: (userId: string, currentIsActive: boolean, userType: string) => Promise<void>;
  onDeleteUser: (userId: string, userType: string) => Promise<void>;
  onSendMessage: (recipientId: string, title: string, message: string) => Promise<void>;
}

const UsersManagement = ({ users, onToggleUserStatus, onDeleteUser, onSendMessage }: UsersManagementProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [messageForm, setMessageForm] = useState({ title: '', message: '', recipientId: '' });
  const { toast } = useToast();

  // Filter users based on search and type
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = userFilter === 'all' || 
                       (userFilter === 'active' && user.status === 'نشط') ||
                       (userFilter === 'inactive' && user.status === 'معطل') ||
                       (userFilter === 'landlord' && user.type === 'مالك') ||
                       (userFilter === 'tenant' && user.type === 'باحث') ||
                       (userFilter === 'service_provider' && user.type === 'صاحب خدمة');
    return matchesSearch && matchesType;
  });

  const handleSendMessage = async () => {
    if (!messageForm.title || !messageForm.message) {
      toast({
        title: "خطأ",
        description: "يرجى ملء جميع الحقول المطلوبة",
        variant: "destructive"
      });
      return;
    }

    await onSendMessage(messageForm.recipientId, messageForm.title, messageForm.message);
    setMessageForm({ title: '', message: '', recipientId: '' });
  };

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          إدارة المستخدمين
        </CardTitle>
        <CardDescription>عرض وإدارة جميع الحسابات المسجلة في المنصة</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Search and Filter Controls */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="البحث عن مستخدم..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-right"
            />
          </div>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="نوع المستخدم" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع المستخدمين</SelectItem>
              <SelectItem value="active">المفعلين</SelectItem>
              <SelectItem value="inactive">غير المفعلين</SelectItem>
              <SelectItem value="landlord">الملاك</SelectItem>
              <SelectItem value="tenant">الباحثين</SelectItem>
              <SelectItem value="service_provider">أصحاب الخدمات</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">الاسم</TableHead>
                <TableHead className="text-right">البريد الإلكتروني</TableHead>
                <TableHead className="text-right">رقم الهاتف</TableHead>
                <TableHead className="text-right">النوع</TableHead>
                <TableHead className="text-right">الحالة</TableHead>
                <TableHead className="text-right">تاريخ التسجيل</TableHead>
                <TableHead className="text-right">العقارات</TableHead>
                <TableHead className="text-right">الإجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} className="hover:bg-muted/50">
                  <TableCell className="text-right font-medium">{user.name}</TableCell>
                  <TableCell className="text-right">{user.email}</TableCell>
                  <TableCell className="text-right">{user.phone || 'غير محدد'}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={
                      user.type === 'مالك' ? 'default' : 
                      user.type === 'صاحب خدمة' ? 'outline' : 'secondary'
                    }>
                      {user.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={user.status === 'نشط' ? 'default' : 'destructive'}>
                      {user.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{user.joinDate}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline">{user.propertiesCount}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-2">
                      {/* Toggle Status */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => onToggleUserStatus(user.userId, user.isActive, user.type)}
                        className="hover:bg-primary hover:text-primary-foreground"
                      >
                        {user.status === 'نشط' ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </Button>
                      
                      {/* Send Message */}
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="hover:bg-info hover:text-info-foreground"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>إرسال رسالة إلى {user.name}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <Input
                              placeholder="عنوان الرسالة"
                              value={messageForm.title}
                              onChange={(e) => setMessageForm({
                                ...messageForm, 
                                title: e.target.value,
                                recipientId: user.id
                              })}
                              className="text-right"
                            />
                            <Textarea
                              placeholder="محتوى الرسالة"
                              value={messageForm.message}
                              onChange={(e) => setMessageForm({
                                ...messageForm, 
                                message: e.target.value
                              })}
                              className="text-right"
                              rows={4}
                            />
                            <Button onClick={handleSendMessage} className="w-full">
                              إرسال الرسالة
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      {/* Delete User */}
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => onDeleteUser(user.userId, user.type)}
                        className="hover:bg-destructive/90"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            لا توجد نتائج للبحث المحدد
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsersManagement;